import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { getPaginationPages } from './utils';
import { Link, Params, useSearchParams } from 'react-router-dom';
import useQueryParams from 'hooks/useQueryParams';
import useMatchMedia from 'hooks/useMatchMedia';
import { PAGE_PARAM_KEYWORD } from 'data/global';

import './ResolvePagination.styles.scss';

type ResolvePaginationProps = {
  className?: string;
  totalPages: number;
  showDisabled?: boolean;
  pageParam?: string;
  onClick?: () => void;
};
const ResolvePagination: React.FC<ResolvePaginationProps> = (props) => {
  const {
    className,
    totalPages,
    showDisabled,
    pageParam = PAGE_PARAM_KEYWORD,
    onClick,
  } = props;

  const [searchParams] = useSearchParams();

  const { params } = useQueryParams<Params>();

  const isAbovePhablet = useMatchMedia('isAbovePhablet');

  const currentPage = +params[pageParam] || 1;
  const prevDisabled = currentPage === 1;
  const nextDisabled = currentPage === totalPages;

  const classes = classNames('resolve-pagination', className);

  const { t } = useTranslation();

  const setTo = useCallback(
    (page: string) => {
      searchParams.set(pageParam, page);

      return {
        pathname: '.',
        search: searchParams.toString(),
      };
    },
    [searchParams, pageParam],
  );

  const renderPages = useMemo(
    () =>
      getPaginationPages(currentPage, +totalPages).map((buttonContent, index) =>
        buttonContent === '...' ? (
          <span key={index}>{buttonContent}</span>
        ) : (
          <Link
            key={index}
            className={classNames({ active: currentPage === buttonContent })}
            to={setTo(buttonContent)}
            onClick={onClick}
          >
            {buttonContent}
          </Link>
        ),
      ),
    [currentPage, totalPages, setTo, onClick],
  );

  if (totalPages <= 1) return null;

  return (
    <div className={classes}>
      {(showDisabled || !prevDisabled) && isAbovePhablet && (
        <Link
          className={classNames({ disabled: prevDisabled }, 'prev-next')}
          to={setTo(String(currentPage - 1))}
          onClick={onClick}
        >
          {t('prev')}
        </Link>
      )}

      {renderPages}

      {(showDisabled || !nextDisabled) && isAbovePhablet && (
        <Link
          className={classNames({ disabled: nextDisabled }, 'prev-next')}
          to={setTo(String(currentPage + 1))}
          onClick={onClick}
        >
          {t('next')}
        </Link>
      )}
    </div>
  );
};

export default ResolvePagination;
